import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getMainAppAuth,
  getStoredUser,
  getStoredWallet,
  setAuth,
} from "../utils/storage";

/*
 * No Aviator login and NO profile / wallet API calls here.
 * Session = main app localStorage (tokenl + userid).
 * Wallet is only updated from the Aviator responses / socket events
 * (bet place, cashout, WALLET_UPDATED) and cached locally.
 */
const AuthContext = createContext(null);

const normalizeWallet = (value) => {
  if (!value || typeof value !== "object") return null;

  const candidates = [value, value.wallet, value.data?.wallet, value.user?.wallet];

  for (const item of candidates) {
    if (!item || typeof item !== "object") continue;

    const raw =
      item.totalBalance ??
      item.balance ??
      item.walletBalance ??
      item.availableBalance ??
      item.available ??
      item.amount ??
      item.cashBalance;

    if (raw !== undefined && raw !== null && raw !== "") {
      return { ...item, totalBalance: Number(raw) || 0 };
    }
  }
  return null;
};

export function AuthProvider({ children }) {
  const [user] = useState(getStoredUser);
  const [wallet, setWallet] = useState(getStoredWallet);

  const { token, userid } = getMainAppAuth();
  const isAuthenticated = Boolean(token && userid);

  const updateWallet = useCallback((next) => {
    const normalized = normalizeWallet(next);
    if (!normalized) return;
    setWallet(normalized);
    setAuth({ wallet: normalized });
  }, []);

  // Kept so existing callers do not break. No network request is made.
  const refreshWallet = useCallback(async () => null, []);
  const logout = useCallback(async () => {}, []);

  const value = useMemo(
    () => ({
      user,
      wallet,
      loading: false,
      isAuthenticated,
      updateWallet,
      refreshWallet,
      logout,
    }),
    [user, wallet, isAuthenticated, updateWallet, refreshWallet, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  return useContext(AuthContext);
}
